import { useContext } from 'react'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal, SidebarNoticeBox,
} from '@domain-group/suit-theme'

import { capitalizeString, digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import QuickLinkListItem from './QuickLinkLIstItem'
import useManageEntityQuickLinksModal from './hooks/useManageEntityQuickLinksModal'

const MODAL_KEY = 'ManageEntityQuickLinksModal'

export type ModalCallbacks = {
  closeModal: () => void,
  updateEntity: (payload: HandleCallbackActionParams) => Promise<object>,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageEntityQuickLinksModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const {
    callbacks: {
      updateEntity: updateFn,
    },
    entity,
    entityKey,
  } = modalPayload

  const hookPayload = useManageEntityQuickLinksModal({
    modalCallbacks: callbacks,
    entity,
    entityKey,
    callbacks: {
      updateFn,
    },
  })
  const {
    actionString,
    callbacks: {
      manageEntityQuickLink,
      selectQuickLink,
    },
    formPayload,
    hasQuickLinks,
    quickLinks,
    updating,
  } = hookPayload

  const {
    entityState,
    entityState: {
      href,
      title,
    },
    handlers,
    saveEnabled,
  } = formPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="small">
      <SidebarModal.Header
        callbacks={callbacks}
        title="My"
        titleSecondLine="Links"
      />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Link Title">
            <input name="title" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Link URL" marginTop="large">
            <input name="href" value={href} {...handlers} />
          </Form.Field>
        </Form>

        <SidebarNoticeBox boxProps={{ marginTop: 'large' }}>
          <SidebarNoticeBox.Title>Adding an external link?</SidebarNoticeBox.Title>

          <SidebarNoticeBox.Paragraph>
            Make sure it has &apos;https://&apos; at the beginning.
          </SidebarNoticeBox.Paragraph>
        </SidebarNoticeBox>

        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => manageEntityQuickLink(actionString, entityState)}
          size="medium"
        >
          {saveEnabled ? `${capitalizeString(actionString)} Link` : 'Complete Fields'}
        </Button>

        {hasQuickLinks && (
          <>
            <SectionDivider marginTop="large">Quick Links</SectionDivider>

            {quickLinks.map(quickLink => (
              <QuickLinkListItem
                callbacks={{ selectQuickLink, manageEntityQuickLink }}
                key={quickLink?.id}
                quickLink={quickLink}
              />
            ))}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close Modal
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageEntityQuickLinksModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
